import { ElementType, HTMLAttributes, ReactNode, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import { MenuProps as MenuPropsInterface } from '@mui/material/Menu';
import Select, { SelectChangeEvent } from '@mui/material/Select';

type Props = {
  name: string;
  children: ReactNode;
  autoFocus?: boolean;
  autoWidth?: boolean;
  defaultValue?: unknown;
  disabled?: boolean;
  displayEmpty?: boolean;
  fullWidth?: boolean;
  helperText?: ReactNode;
  id?: string;
  IconComponent?: ElementType;
  label?: ReactNode;
  margin?: 'none' | 'dense' | 'normal';
  MenuProps?: Partial<MenuPropsInterface>;
  multiple?: boolean;
  
  renderValue?: (value: any) => ReactNode;
  required?: boolean;
  SelectDisplayProps?: HTMLAttributes<HTMLDivElement>;
  size?: 'small' | 'medium' | 'large';
  variant?: 'standard' | 'outlined' | 'filled';
  onChange?: (event: SelectChangeEvent) => void;
  sx?: object;
};

const HookFormSelect = ({
  name,
  children,
  onChange: onChangeExternal,
  autoFocus = false,
  autoWidth = false,
  defaultValue = '',
  disabled = false,
  displayEmpty = false,
  fullWidth = true,
  helperText = '',
  IconComponent = undefined,
  id = undefined,
  label = '',
  // 'none' so the control lines up with its neighbours in a filter row; the
  // old 'normal' default added 16px/8px of vertical margin that only this
  // wrapper had.
  margin = 'none',
  MenuProps = undefined,
  multiple = false,
  renderValue = undefined,
  required = false,
  SelectDisplayProps = undefined,
  size = 'small',
  variant = 'outlined',
  sx = {},
}: Props) => {
  const {
    formState: { errors },
  } = useFormContext();
  const error = errors[name];
  const displayError = Boolean(error?.message);
  const inputLabel = useRef<HTMLLabelElement>(null);

  const controlId = id ?? name;
  const labelId = `${controlId}-label`;

  return (
    <FormControl
      disabled={disabled}
      error={displayError}
      fullWidth={fullWidth}
      margin={margin}
      required={required}
      size={size}
      variant={variant}
    >
      <InputLabel ref={inputLabel} id={labelId}>
        {label}
      </InputLabel>
      <Controller
        name={name}
        defaultValue={defaultValue}
        render={({ field: { onChange, onBlur, value } }) => (
          <Select
            autoFocus={autoFocus}
            autoWidth={autoWidth}
            displayEmpty={displayEmpty}
            IconComponent={IconComponent}
            id={controlId}
            labelId={labelId}
            label={label}
            MenuProps={MenuProps}
            multiple={multiple}
            name={name}
            onBlur={onBlur}
            onChange={(e) => {
              onChange(e);
              if (onChangeExternal) onChangeExternal(e);
            }}
            renderValue={renderValue}
            SelectDisplayProps={SelectDisplayProps}
            value={value}
            sx={sx}
          >
            {children}
          </Select>
        )}
      />
      {/* Only render when there is something to say — an empty helper still
          reserves a line box, which knocked this control out of alignment. */}
      {(displayError || helperText) && (
        <FormHelperText>{displayError ? String(error.message) : helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default HookFormSelect;
